import { useEffect, useState, useCallback } from "react";
import { useHub } from "../../context/HubContext";
import { getHubPartners } from "../../services/hubPartnerService";
import { formatDate } from "../../utils/formatters";
import PageHeader from "../../components/common/PageHeader";
import Avatar from "../../components/common/Avatar";
import Badge from "../../components/common/Badge";
import InvitePartnerModal from "../../components/ingroove/partners/InvitePartnerModal";
import RemovePartnerModal from "../../components/ingroove/partners/RemovePartnerModal";
import { Search, UserPlus, Handshake, Trash2 } from "lucide-react";

const roleBadgeVariant = (role) => {
  switch (role?.toLowerCase()) {
    case "admin":
      return "danger";
    case "operations":
      return "info";
    case "finance":
      return "warning";
    case "content":
      return "success";
    default:
      return "default";
  }
};

const HubPartners = () => {
  const { hub } = useHub();
  const [partners, setPartners] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);

  const fetchPartners = useCallback(async () => {
    if (!hub?.id) return;
    setLoading(true);
    try {
      const res = await getHubPartners(hub.id);
      setPartners(res);
      setFiltered(res);
    } finally {
      setLoading(false);
    }
  }, [hub?.id]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  // Client-side search
  useEffect(() => {
    if (!search) {
      setFiltered(partners);
      return;
    }
    const s = search.toLowerCase();
    setFiltered(
      partners.filter(
        (p) =>
          p.user?.name?.toLowerCase().includes(s) ||
          p.user?.surname?.toLowerCase().includes(s) ||
          p.user?.email?.toLowerCase().includes(s) ||
          p.role?.name?.toLowerCase().includes(s),
      ),
    );
  }, [search, partners]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Hub Partners"
        subtitle={`${partners.length} partner${partners.length !== 1 ? "s" : ""} in your hub`}
        action={
          <button
            onClick={() => setShowInvite(true)}
            className="flex items-center gap-2 px-4 py-2 ingroove-gradient-bg text-white text-xs font-semibold rounded-xl hover:opacity-90 transition shadow-glow"
          >
            <UserPlus size={14} />
            Invite Partner
          </button>
        }
      />

      {/* Search */}
      <div className="relative max-w-sm">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
        />
        <input
          type="text"
          placeholder="Search by name, email or role..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 text-sm bg-bg-surface border border-border rounded-xl text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition"
        />
      </div>

      {/* Table */}
      <div className="bg-bg-surface border border-border rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-[2fr_1fr_1fr_auto] gap-4 px-5 py-3 bg-bg-elevated text-xs text-text-muted uppercase tracking-wide border-b border-border">
          <span>Partner</span>
          <span>Role</span>
          <span>Joined</span>
          <span />
        </div>

        {loading ? (
          <div>
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="grid grid-cols-[2fr_1fr_1fr_auto] gap-4 items-center px-5 py-4 border-b border-border animate-pulse"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-bg-elevated flex-shrink-0" />
                  <div className="space-y-1.5 flex-1">
                    <div className="h-3 bg-bg-elevated rounded w-28" />
                    <div className="h-2.5 bg-bg-elevated rounded w-40" />
                  </div>
                </div>
                <div className="h-5 bg-bg-elevated rounded-full w-20" />
                <div className="h-3 bg-bg-elevated rounded w-20" />
                <div className="h-7 bg-bg-elevated rounded-xl w-7" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Handshake size={28} className="text-border-hover mb-2" />
            <p className="text-xs text-text-muted mb-3">
              {search ? "No partners match your search" : "No partners yet"}
            </p>
            {!search && (
              <button
                onClick={() => setShowInvite(true)}
                className="text-xs text-primary hover:opacity-80 transition"
              >
                Invite your first partner →
              </button>
            )}
          </div>
        ) : (
          <div>
            {filtered.map((partner) => (
              <div
                key={partner.id}
                className="grid grid-cols-[2fr_1fr_1fr_auto] gap-4 items-center px-5 py-4 border-b border-border last:border-0 hover:bg-bg-elevated transition"
              >
                {/* Partner */}
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar
                    name={partner.user?.name}
                    surname={partner.user?.surname}
                    image={partner.user?.image}
                    size="md"
                  />
                  <div className="min-w-0">
                    <p className="text-sm text-text-primary font-medium truncate">
                      {partner.user?.name} {partner.user?.surname}
                    </p>
                    <p className="text-xs text-text-muted truncate">
                      {partner.user?.email}
                    </p>
                  </div>
                </div>

                {/* Role */}
                <div>
                  <Badge
                    label={partner.role?.name ?? "—"}
                    variant={roleBadgeVariant(partner.role?.name)}
                  />
                </div>

                {/* Joined */}
                <div className="text-xs text-text-muted">
                  {formatDate(partner.createdDate ?? partner.updatedDate)}
                </div>

                {/* Remove — hide for Admin role */}
                <div>
                  {partner.role?.name?.toLowerCase() !== "admin" && (
                    <button
                      onClick={() => setRemoveTarget(partner)}
                      className="p-1.5 rounded-lg text-text-muted hover:text-red-400 hover:bg-red-500/10 transition"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invite modal */}
      {showInvite && (
        <InvitePartnerModal
          hubId={hub?.id}
          onClose={() => setShowInvite(false)}
          onSuccess={fetchPartners}
        />
      )}

      {/* Remove modal */}
      {removeTarget && (
        <RemovePartnerModal
          partner={removeTarget}
          onClose={() => setRemoveTarget(null)}
          onSuccess={fetchPartners}
        />
      )}
    </div>
  );
};

export default HubPartners;
